import { useEffect, useRef, useState } from 'react';
import { tr } from '../../lib/i18n';
import { portableValable } from '../../lib/portable';
import { C } from '../../theme/palette';
import { initiales, libelleChantier, PASTILLES } from './listes';
import { alerte, bouton, carte, champ, etiquette } from './styles';

/** L'orange du chantier, celui de la maquette. */
const AMBRE = '#D97706';

/**
 * Les trois étapes de la commande — le chantier, le fournisseur, les
 * articles —, la courante en orange ; une étape faite se rouvre d'un appui.
 *
 * @param {object} props propriétés
 * @param {'chantier'|'fournisseur'|'articles'} props.etape étape en cours
 * @param {(etape: string) => void} props.vers rouvre une étape faite
 * @returns {import('react').ReactElement} l'indicateur
 */
export function EtapesCommande({ etape, vers }) {
  const ordre = ['chantier', 'fournisseur', 'articles'];
  const libelles = {
    chantier: tr('Chantier'),
    fournisseur: tr('Fournisseur'),
    articles: tr('Articles'),
  };
  const rang = ordre.indexOf(etape);
  return (
    <nav
      aria-label={tr('Étapes de la commande')}
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: 6,
        marginBottom: 10,
      }}
    >
      {ordre.map((cle, i) => {
        const faite = i < rang;
        const courante = i === rang;
        const style = {
          textAlign: 'center',
          borderRadius: 8,
          padding: '8px 4px',
          minHeight: 36,
          fontSize: 12,
          fontWeight: 800,
          border: 'none',
          fontFamily: 'inherit',
          background: courante ? AMBRE : faite ? '#E8F3EC' : '#E5E7EB',
          color: courante ? '#fff' : faite ? '#14532D' : '#374151',
          overflowWrap: 'anywhere',
        };
        return faite ? (
          <button key={cle} onClick={() => vers(cle)} style={{ ...style, cursor: 'pointer' }}>
            ✓ {libelles[cle]}
          </button>
        ) : (
          <span key={cle} aria-current={courante ? 'step' : undefined} style={style}>
            {i + 1} · {libelles[cle]}
          </span>
        );
      })}
    </nav>
  );
}

/**
 * Le chantier choisi, en haut pendant toute la commande : son numéro, son
 * nom, l'adresse où livrer.
 *
 * @param {object} props propriétés
 * @param {object} props.chantier le chantier choisi
 * @returns {import('react').ReactElement} le bandeau
 */
export function BandeauChantier({ chantier }) {
  return (
    <div
      data-testid="chantier-choisi"
      style={{
        background: '#FFF7ED',
        border: '2px solid ' + AMBRE,
        borderRadius: 12,
        padding: '8px 12px',
        marginBottom: 10,
        color: '#7C2D12',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 900 }}>
        🏗 <span translate="no">{libelleChantier(chantier.numero, chantier.nom)}</span>
      </div>
      <div style={{ fontSize: 12, lineHeight: 1.4 }} translate="no">
        {chantier.adresse}
        {chantier.precision ? ' — ' + chantier.precision : ''}
      </div>
    </div>
  );
}

/**
 * Étape 1 : pour quel chantier ? Ceux de l'entreprise, d'un appui ; un autre,
 * saisi en entier — l'adresse exacte de livraison au moins — avec le
 * portable à appeler sur place.
 *
 * @param {object} props propriétés
 * @param {object[]|null} props.chantiers chantiers de l'entreprise, null pendant la lecture
 * @param {string} [props.telephone] portable de la personne, proposé pour le chantier saisi
 * @param {(chantier: object) => void} props.choisit retient le chantier
 * @returns {import('react').ReactElement} le choix
 */
export function ChoixChantier({ chantiers, telephone, choisit }) {
  const [f, setF] = useState({ numero: '', nom: '', adresse: '', telephone: telephone || '' });
  const [erreur, setErreur] = useState('');
  // Le portable arrive avec le profil : proposé tant qu'on n'a rien tapé.
  const telTape = useRef(false);
  useEffect(() => {
    if (telephone && !telTape.current) setF((x) => ({ ...x, telephone }));
  }, [telephone]);
  const maj = (cle) => (e) => {
    if (cle === 'telephone') telTape.current = true;
    setF((x) => ({ ...x, [cle]: e.target.value }));
  };
  const continuer = (e) => {
    e.preventDefault();
    if (!f.nom.trim() || !f.adresse.trim()) {
      setErreur(tr('Indiquez le nom du chantier et l’adresse exacte de livraison.'));
      return;
    }
    if (f.telephone.trim() && !portableValable(f.telephone)) {
      setErreur(tr('Indiquez un numéro de portable valable : au moins 9 chiffres.'));
      return;
    }
    setErreur('');
    choisit({
      id: null,
      numero: f.numero.trim(),
      nom: f.nom.trim(),
      adresse: f.adresse.trim(),
      telephone: f.telephone.trim(),
    });
  };
  return (
    <section aria-labelledby="titre-chantier">
      <h2 id="titre-chantier" style={{ fontSize: 17, margin: '2px 0 4px' }}>
        Pour quel chantier ?
      </h2>
      {chantiers === null && <div style={{ ...carte, color: C.muted }}>Chargement…</div>}
      {chantiers && chantiers.length > 0 && (
        <p style={{ fontSize: 13, color: C.grey, margin: '0 0 8px' }}>
          Les chantiers de votre entreprise : un appui suffit.
        </p>
      )}
      {(chantiers || []).map((ch) => (
        <button
          key={ch.id}
          onClick={() => choisit({ ...ch, telephone: '' })}
          style={{
            ...carte,
            display: 'block',
            width: '100%',
            textAlign: 'left',
            border: '1.5px solid ' + C.border,
            fontFamily: 'inherit',
            color: C.ink,
            cursor: 'pointer',
          }}
        >
          <b style={{ display: 'block', fontSize: 15 }} translate="no">
            {libelleChantier(ch.numero, ch.nom)}
          </b>
          <span style={{ fontSize: 12, color: C.grey }} translate="no">
            {ch.adresse}
          </span>
        </button>
      ))}
      <form
        onSubmit={continuer}
        style={{ ...carte, border: '1.5px dashed #9CA3AF', boxShadow: 'none' }}
        aria-label={tr('Chantier pas dans la liste ?')}
      >
        <b style={{ display: 'block', fontSize: 14, marginBottom: 8 }}>
          Chantier pas dans la liste ?
        </b>
        <div style={{ display: 'grid', gap: 8 }}>
          <label style={etiquette}>
            N° de chantier
            <input
              style={champ}
              maxLength={40}
              placeholder={tr('ex. 2026-021')}
              value={f.numero}
              onChange={maj('numero')}
            />
          </label>
          <label style={etiquette}>
            Nom du chantier
            <input
              style={champ}
              maxLength={160}
              placeholder={tr('ex. École des Palettes')}
              value={f.nom}
              onChange={maj('nom')}
            />
          </label>
          <label style={etiquette}>
            Adresse exacte de livraison (obligatoire)
            <input
              style={champ}
              maxLength={300}
              placeholder={tr('Rue, n°, NPA, localité')}
              value={f.adresse}
              onChange={maj('adresse')}
            />
          </label>
          <label style={etiquette}>
            Téléphone sur place (pour le livreur)
            <input
              style={champ}
              type="tel"
              inputMode="tel"
              maxLength={40}
              value={f.telephone}
              onChange={maj('telephone')}
            />
          </label>
        </div>
        {erreur && (
          <div role="alert" style={alerte}>
            {erreur}
          </div>
        )}
        <button type="submit" style={{ ...bouton(C.green), width: '100%', marginTop: 10 }}>
          Continuer ›
        </button>
      </form>
    </section>
  );
}

/**
 * Étape 2 : chez quel fournisseur ? Les magasins où la personne est cliente ;
 * en choisir un autre que celui ouvert l'ouvre, comme « ⇄ Changer ».
 *
 * @param {object} props propriétés
 * @param {object[]} props.fournisseurs magasins dont la personne est cliente
 * @param {(fournisseur: object) => Promise<void>|void} props.choisit ouvre ce magasin
 * @returns {import('react').ReactElement} le choix
 */
export function ChoixFournisseur({ fournisseurs, choisit }) {
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState('');
  const ouvre = async (s) => {
    setEnCours(s.id);
    setErreur('');
    try {
      await choisit(s);
    } catch (e) {
      setErreur(e?.message || tr('Ce magasin n’a pas pu s’ouvrir. Réessayez.'));
      setEnCours(null);
    }
  };
  return (
    <section aria-labelledby="titre-fournisseur">
      <h2 id="titre-fournisseur" style={{ fontSize: 17, margin: '2px 0 8px' }}>
        Chez quel fournisseur ?
      </h2>
      {fournisseurs.map((s, i) => {
        const [fond, encre] = PASTILLES[i % PASTILLES.length];
        return (
          <button
            key={s.id}
            onClick={() => ouvre(s)}
            disabled={enCours != null}
            style={{
              ...carte,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              width: '100%',
              textAlign: 'left',
              border: '1.5px solid ' + (s.courante ? C.teal : C.border),
              fontFamily: 'inherit',
              color: C.ink,
              cursor: 'pointer',
            }}
          >
            <span
              aria-hidden="true"
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: fond,
                color: encre,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 900,
                flexShrink: 0,
              }}
            >
              {initiales(s.nom)}
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <b style={{ display: 'block', fontSize: 15, overflowWrap: 'anywhere' }}>{s.nom}</b>
              {s.courante && (
                <span style={{ fontSize: 12, color: C.teal, fontWeight: 700 }}>● Ouvert</span>
              )}
            </span>
            <span aria-hidden="true" style={{ fontSize: 20, color: C.muted }}>
              {enCours === s.id ? '…' : '›'}
            </span>
          </button>
        );
      })}
      {erreur && (
        <div role="alert" style={alerte}>
          {erreur}
        </div>
      )}
    </section>
  );
}
